import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { track } from "../lib/analytics";
import SignupPromptModal from "../components/SignupPromptModal";

const CATEGORY_OPTIONS = [
  { value: "park", label: "Dog Park" },
  { value: "beach", label: "Dog-Friendly Beach" },
  { value: "restaurant", label: "Dog-Friendly Restaurant/Bar" },
  { value: "groomer", label: "Groomer" },
  { value: "vet", label: "Vet" },
  { value: "boarding", label: "Boarding/Daycare" },
  { value: "trainer", label: "Trainer" },
  { value: "pet_store", label: "Pet Store" },
  { value: "trail", label: "Trail" },
  { value: "private_park", label: "Private Park" },
  { value: "other", label: "Other" },
];

const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["DC","District of Columbia"],
  ["FL","Florida"],["GA","Georgia"],["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],
  ["IN","Indiana"],["IA","Iowa"],["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],
  ["ME","Maine"],["MD","Maryland"],["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],
  ["MS","Mississippi"],["MO","Missouri"],["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],
  ["NH","New Hampshire"],["NJ","New Jersey"],["NM","New Mexico"],["NY","New York"],
  ["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],["OK","Oklahoma"],["OR","Oregon"],
  ["PA","Pennsylvania"],["PR","Puerto Rico"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
];

const WHY_MAX = 500;

const initialForm = {
  name: "",
  address: "",
  city: "",
  state: "",
  category: "",
  why_dog_friendly: "",
  website: "",
  phone: "",
};

/**
 * Full-page community-submission form. Lives at `/suggest`.
 *
 * Auth: gated. Unauthenticated users see the existing SignupPromptModal
 * with a custom CTA ("Sign up to suggest a dog-friendly place!"). Once
 * logged in, the form renders. On successful submit, the auto-approved
 * place gets a confirmation card + deep-link to its detail page.
 *
 * GA4:
 *  - `suggest_place_form_viewed` once when the page loads (authed)
 *  - `suggest_place_submitted` on success with {category, city, state}
 */
const SuggestPlace = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Fire the impression event ONCE per page load (and only for
  // authenticated users — the SignupPromptModal-only state is a
  // pre-funnel impression, not a form view).
  useEffect(() => {
    if (!authLoading && user) {
      track("suggest_place_form_viewed");
    }
  }, [authLoading, user]);

  // Page-level meta tags (matches the pattern used by GuideDetail.jsx —
  // we mutate <head> directly instead of pulling in react-helmet-async,
  // which isn't installed). Title + description live for the lifetime
  // of the page; on unmount we don't reset because the next mounted
  // page sets its own.
  useEffect(() => {
    const SITE_URL = "https://pawground.app";
    document.title = "Suggest a Dog-Friendly Place | Pawground";
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        if (selector.startsWith("meta[")) {
          el = document.createElement("meta");
          const propMatch = selector.match(/property="([^"]+)"/);
          const nameMatch = selector.match(/name="([^"]+)"/);
          if (propMatch) el.setAttribute("property", propMatch[1]);
          else if (nameMatch) el.setAttribute("name", nameMatch[1]);
        } else if (selector.startsWith("link[")) {
          el = document.createElement("link");
          const relMatch = selector.match(/rel="([^"]+)"/);
          if (relMatch) el.setAttribute("rel", relMatch[1]);
        } else {
          return;
        }
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    const desc = "Know a dog-friendly spot that's missing? Submit it to Pawground and help local dog parents discover new places.";
    setMeta('meta[name="description"]', "content", desc);
    setMeta('link[rel="canonical"]', "href", `${SITE_URL}/suggest`);
    setMeta('meta[property="og:title"]', "content", "Suggest a Dog-Friendly Place | Pawground");
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:url"]', "content", `${SITE_URL}/suggest`);
    setMeta('meta[name="twitter:title"]', "content", "Suggest a Dog-Friendly Place | Pawground");
    setMeta('meta[name="twitter:description"]', "content", desc);
  }, []);

  const whyLeft = useMemo(() => WHY_MAX - form.why_dog_friendly.length, [form.why_dog_friendly]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Place name is required";
    if (form.address.trim().length < 4) e.address = "Please add a full street address";
    if (form.city.trim().length < 2) e.city = "City is required";
    if (!form.state) e.state = "State is required";
    if (!form.category) e.category = "Pick a category";
    if (form.why_dog_friendly.trim().length < 10) e.why_dog_friendly = "Tell us a bit more (at least 10 chars)";
    if (form.why_dog_friendly.length > WHY_MAX) e.why_dog_friendly = `Keep it under ${WHY_MAX} chars`;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/places/suggest", {
        name: form.name.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state,
        category: form.category,
        why_dog_friendly: form.why_dog_friendly.trim(),
        website: form.website.trim() || null,
        phone: form.phone.trim() || null,
      });
      track("suggest_place_submitted", {
        category: form.category,
        city: form.city.trim().toLowerCase(),
        state: form.state,
      });
      setSuccess({
        message: data.message || "🐾 Thanks for the tip! Your place is now live on Pawground.",
        url: data.url || `/place/${data.slug}`,
        name: form.name.trim(),
      });
      setForm(initialForm);
      // Reset scroll so the success card is fully visible on mobile.
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // 409 → duplicate detection; let the API copy speak directly.
      if (err?.response?.status === 409 && typeof detail === "string") {
        toast.error(detail);
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Couldn't submit your place. Try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Show the signup prompt for unauthenticated users; we still render
  // the static shell beneath it so search engines see the page content.
  const showAuthGate = !authLoading && !user;

  return (
    <>
      <div className="min-h-screen" style={{ backgroundColor: "#F5F0E8" }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
          <div className="mb-8">
            <h1
              className="font-heading"
              style={{ fontSize: "24px", color: "#1A1A0F", fontWeight: 600, lineHeight: 1.2 }}
            >
              Suggest a Dog-Friendly Place
            </h1>
            <p
              className="mt-2 font-body"
              style={{ fontSize: "14px", color: "#7A7A60", lineHeight: 1.5 }}
            >
              Know a spot that's missing from Pawground? Help fellow dog parents discover it.
            </p>
          </div>

          {success && (
            <div
              data-testid="suggest-place-success"
              className="mb-8 rounded-xl border p-5 flex items-start gap-3"
              style={{ backgroundColor: "#E8F2EC", borderColor: "#4A7C59" }}
              role="status"
              aria-live="polite"
            >
              <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5" style={{ color: "#4A7C59" }} />
              <div className="flex-1">
                <p className="font-body font-semibold" style={{ color: "#1A1A0F", fontSize: "15px" }}>
                  {success.message}
                </p>
                <Link
                  to={success.url}
                  data-testid="suggest-place-view-link"
                  className="mt-2 inline-flex items-center gap-1.5 font-body font-semibold text-sm"
                  style={{ color: "#4A7C59" }}
                >
                  View {success.name}
                  <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          )}

          {!showAuthGate && (
            <form
              data-testid="suggest-place-form"
              onSubmit={onSubmit}
              className="space-y-5 bg-white rounded-2xl border p-5 sm:p-7"
              style={{ borderColor: "#D4C9B0" }}
            >
              <Field label="Place Name" required error={errors.name}>
                <input
                  type="text"
                  data-testid="suggest-field-name"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="e.g., Joe's Coffee Patio"
                  className="form-input"
                  maxLength={120}
                />
              </Field>
              <Field label="Address" required error={errors.address}>
                <input
                  type="text"
                  data-testid="suggest-field-address"
                  value={form.address}
                  onChange={(e) => setField("address", e.target.value)}
                  placeholder="e.g., 123 Main St, Austin, TX 78701"
                  className="form-input"
                  maxLength={200}
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="City" required error={errors.city}>
                  <input
                    type="text"
                    data-testid="suggest-field-city"
                    value={form.city}
                    onChange={(e) => setField("city", e.target.value)}
                    placeholder="e.g., Austin"
                    className="form-input"
                    maxLength={80}
                  />
                </Field>
                <Field label="State" required error={errors.state}>
                  <select
                    data-testid="suggest-field-state"
                    value={form.state}
                    onChange={(e) => setField("state", e.target.value)}
                    className="form-input"
                  >
                    <option value="">Choose a state…</option>
                    {US_STATES.map(([code, name]) => (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="Category" required error={errors.category}>
                <select
                  data-testid="suggest-field-category"
                  value={form.category}
                  onChange={(e) => setField("category", e.target.value)}
                  className="form-input"
                >
                  <option value="">Choose a category…</option>
                  {CATEGORY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Why is this place dog-friendly?"
                required
                error={errors.why_dog_friendly}
                hint={
                  <span style={{ color: whyLeft < 30 ? "#B45309" : "#7A7A60" }}>
                    {whyLeft} characters left
                  </span>
                }
              >
                <textarea
                  data-testid="suggest-field-why"
                  value={form.why_dog_friendly}
                  onChange={(e) => setField("why_dog_friendly", e.target.value.slice(0, WHY_MAX))}
                  placeholder="e.g., They have a fenced patio area and bring out water bowls for dogs. Super welcoming staff!"
                  rows={4}
                  className="form-input resize-y"
                  maxLength={WHY_MAX}
                />
              </Field>
              <Field label="Website" hint="Optional">
                <input
                  type="url"
                  data-testid="suggest-field-website"
                  value={form.website}
                  onChange={(e) => setField("website", e.target.value)}
                  placeholder="https://"
                  className="form-input"
                  maxLength={300}
                />
              </Field>
              <Field label="Phone" hint="Optional">
                <input
                  type="tel"
                  data-testid="suggest-field-phone"
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  placeholder="(555) 555-5555"
                  className="form-input"
                  maxLength={40}
                />
              </Field>

              <button
                type="submit"
                data-testid="suggest-place-submit"
                disabled={submitting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 font-body font-semibold rounded-lg active:scale-[0.97] transition-transform disabled:opacity-60 disabled:cursor-wait"
                style={{
                  backgroundColor: "#4A7C59",
                  color: "#FFFFFF",
                  padding: "12px 24px",
                  fontSize: "14px",
                  minWidth: "180px",
                }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>Submit Place</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>

      {showAuthGate && (
        <SignupPromptModal
          open
          trigger="suggest_place"
          onClose={() => navigate("/")}
        />
      )}

      {/* Scoped form styling per spec. We avoid Tailwind utility classes
          here so the spec-exact 1px #D4C9B0 border / 8px radius / DM Sans
          14px / focus #4A7C59 always wins, regardless of where the form
          is embedded later. */}
      <style>{`
        .form-input {
          width: 100%;
          background: #FFFFFF;
          border: 1px solid #D4C9B0;
          border-radius: 8px;
          padding: 12px;
          font-family: 'DM Sans', system-ui, sans-serif;
          font-size: 14px;
          color: #1A1A0F;
          transition: border-color 120ms ease-out, box-shadow 120ms ease-out;
        }
        .form-input::placeholder { color: #9C9C7A; }
        .form-input:focus {
          outline: none;
          border-color: #4A7C59;
          box-shadow: 0 0 0 3px rgba(74,124,89,0.18);
        }
        .form-input[aria-invalid="true"] {
          border-color: #C0392B;
        }
        .form-input[aria-invalid="true"]:focus {
          box-shadow: 0 0 0 3px rgba(192,57,43,0.18);
        }
      `}</style>
    </>
  );
};

const Field = ({ label, required, hint, error, children }) => {
  // Inject aria-invalid onto the form control when there's an error.
  // We can't add it to <input>/<select>/<textarea> directly because
  // the parent owns the value/onChange — clone instead.
  const child = React.Children.only(children);
  const enhanced = React.cloneElement(child, {
    "aria-invalid": error ? "true" : undefined,
  });
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span
          className="font-body font-semibold"
          style={{ fontSize: "13px", color: "#1A1A0F" }}
        >
          {label}
          {required && <span style={{ color: "#C0392B" }} aria-hidden="true"> *</span>}
        </span>
        {hint && (
          <span
            className="font-body"
            style={{ fontSize: "12px", color: "#7A7A60" }}
          >
            {hint}
          </span>
        )}
      </div>
      {enhanced}
      {error && (
        <p
          className="mt-1.5 font-body"
          style={{ fontSize: "12px", color: "#C0392B" }}
          role="alert"
        >
          {error}
        </p>
      )}
    </label>
  );
};

export default SuggestPlace;
